<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { CheckCircle2, CircleHelp, XCircle, ExternalLink, Wallet, User, ArrowLeft, Landmark, Loader2, RefreshCw, ChevronDown, ChevronUp, Eye, EyeOff, Clipboard } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { API, HOST } from '@/utils/http'
import { useToast } from '@/components/ui/toast/use-toast'
import type { BridgeOrganization, BridgeCustomer, BridgeTransfer, ExternalAccount, CombinedCustomer, BridgeLink, VirtualAccount, CryptoAddress } from '@/types/bridge'
import { isBridgeUrlSafe } from '@/config/bridge'
import type OrgMember from '@/types/OrgMember'
import OrgKYBOnboarding from './OrgKYBOnboarding.vue'
import OrgPaymentsMembersList from './OrgPaymentsMembersList.vue'
import MemberKycSteps from './MemberKycSteps.vue'
import BridgeTransfersList from '@/pages/Admin/Payments/BridgeTransfersList.vue'
import ConnectExternalAccountForm from '@/pages/Admin/Payments/ConnectExternalAccountForm.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import InvoicesTab from './InvoicesTab.vue'

const { toast } = useToast()
const store = useStore()
const route = useRoute()
const router = useRouter()
const member = computed(() => store.getters['getMember'])
const isSuperUser = computed(() => member.value?.is_super_user)
const currentOrg = computed(() => store.getters['org/getSelectedOrg'])
const isOrgAdmin = computed(() =>
  !isSuperUser.value && currentOrg.value?.current_member?.role === 'org_admin'
)
const orgId = computed(() => parseInt(route.params.org_id as string))

// Super-admins can toggle into their own KYC view; also auto-activated via ?mode=kyc or /kyc route
const selfMode = ref(route.query.mode === 'kyc' || route.name === 'OrgKyc')

watch(
  () => [route.name, route.query.mode] as const,
  ([name, mode]) => {
    if (mode === 'kyc' || name === 'OrgKyc') {
      selfMode.value = true
    } else if (name === 'OrgPayments') {
      selfMode.value = false
    }
  }
)

const bridgeOrg = ref<BridgeOrganization | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const view = ref<'status' | 'kyb'>('status')
const VALID_TABS = ['business', 'people', 'transfers', 'invoices'] as const
type PaymentsTab = typeof VALID_TABS[number]

const hashTab = route.hash.slice(1) as PaymentsTab
const initialTab = VALID_TABS.includes(hashTab) ? hashTab : 'business'
const activeTab = ref<PaymentsTab>(initialTab)
const balance = ref<{ wallet_id: string | null; address: string | null; balances: any[] } | null>(null)
const balanceLoading = ref(false)

// Unified withdrawal
const showWithdrawForm = ref(false)
const withdrawConfirming = ref(false)
const withdrawAmount = ref('')
const withdrawDest = ref('')  // encoded: "ach:<external_account_id>" | "crypto:<crypto_address_id>"
const withdrawIdempotencyKey = ref('')
const withdrawLoading = ref(false)
const withdrawError = ref<string | null>(null)
const withdrawSuccess = ref(false)

// Crypto addresses
const cryptoAddresses = ref<CryptoAddress[]>([])
const cryptoAddressesLoading = ref(false)
const showAddCryptoForm = ref(false)
const newCryptoAddress = ref('')
const newCryptoChain = ref<'base' | 'polygon'>('base')
const newCryptoLabel = ref('')
const addCryptoLoading = ref(false)
const addCryptoError = ref<string | null>(null)

const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
const newCryptoAddressValid = computed(() => EVM_ADDRESS_RE.test(newCryptoAddress.value))

const fetchCryptoAddresses = async () => {
  if (!bridgeOrg.value) return
  cryptoAddressesLoading.value = true
  try {
    const res = await API.get(`${HOST}/payments/bridge/customers/${bridgeOrg.value.id}/crypto-addresses`)
    cryptoAddresses.value = Array.isArray(res.data) ? res.data : []
  } catch {
    cryptoAddresses.value = []
  } finally {
    cryptoAddressesLoading.value = false
  }
}

const submitAddCryptoAddress = async () => {
  addCryptoError.value = null
  if (!bridgeOrg.value) {
    addCryptoError.value = 'Organization details are still loading. Please try again.'
    return
  }
  addCryptoLoading.value = true
  try {
    await API.post(`${HOST}/payments/bridge/customers/${bridgeOrg.value.id}/crypto-addresses`, {
      address: newCryptoAddress.value,
      chain: newCryptoChain.value,
      label: newCryptoLabel.value || null,
    })
    newCryptoAddress.value = ''
    newCryptoChain.value = 'base'
    newCryptoLabel.value = ''
    showAddCryptoForm.value = false
    await fetchCryptoAddresses()
  } catch (err: any) {
    addCryptoError.value = err.response?.data?.detail || 'Failed to save address.'
  } finally {
    addCryptoLoading.value = false
  }
}

const confirmVerifyDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingVerifyAddressId = ref<number | null>(null)
const pendingVerifyValue = ref(true)

const promptVerifyCryptoAddress = (addressId: number, verified: boolean) => {
  pendingVerifyAddressId.value = addressId
  pendingVerifyValue.value = verified
  confirmVerifyDialog.value?.open()
}

const verifyCryptoAddress = async () => {
  if (!bridgeOrg.value || pendingVerifyAddressId.value === null) return
  const addressId = pendingVerifyAddressId.value
  const verified = pendingVerifyValue.value
  try {
    const res = await API.patch(
      `${HOST}/payments/bridge/customers/${bridgeOrg.value.id}/crypto-addresses/${addressId}/verify`,
      { verified },
    )
    const idx = cryptoAddresses.value.findIndex(a => a.id === addressId)
    if (idx !== -1) cryptoAddresses.value[idx] = res.data
  } catch (err: any) {
    const action = verified ? 'Verify' : 'Unverify'
    toast({ title: `${action} failed`, description: err.response?.data?.detail || 'Could not update address.', variant: 'destructive' })
  }
}

const activeExternalAccounts = computed(() =>
  externalAccounts.value.filter(ea => ea.active && ea.account_type === 'us')
)

const hasAnyWithdrawDest = computed(() =>
  activeExternalAccounts.value.length > 0 || cryptoAddresses.value.length > 0
)

const availableBalance = computed(() =>
  parseFloat(balance.value?.balances?.find((b: any) => b.currency === 'usdb')?.balance ?? '0')
)

const withdrawAmountNum = computed(() => parseFloat(withdrawAmount.value) || 0)

const withdrawDestParsed = computed(() => {
  if (!withdrawDest.value) return null
  const [type, id] = withdrawDest.value.split(':')
  if (type === 'ach') {
    const account = activeExternalAccounts.value.find(ea => ea.id === id) ?? null
    return account ? { type: 'ach' as const, account } : null
  }
  if (type === 'crypto') {
    const account = cryptoAddresses.value.find(ca => ca.id === parseInt(id)) ?? null
    return account ? { type: 'crypto' as const, account } : null
  }
  return null
})

const openWithdrawForm = () => {
  withdrawConfirming.value = false
  withdrawIdempotencyKey.value = ''
  withdrawError.value = null
  withdrawSuccess.value = false
  withdrawDest.value = ''
  withdrawAmount.value = ''
  showWithdrawForm.value = true
}

const cancelWithdraw = () => {
  withdrawConfirming.value = false
  withdrawIdempotencyKey.value = ''
  withdrawError.value = null
}

const reviewWithdraw = () => {
  withdrawError.value = null
  if (!withdrawIdempotencyKey.value) withdrawIdempotencyKey.value = crypto.randomUUID()
  withdrawConfirming.value = true
}

const submitWithdraw = async () => {
  withdrawError.value = null
  const dest = withdrawDestParsed.value
  if (!dest) {
    withdrawError.value = 'Selected destination is no longer available. Please re-select.'
    return
  }
  withdrawLoading.value = true
  try {
    if (dest.type === 'ach') {
      await API.post(`${HOST}/payments/bridge/organizations/${orgId.value}/withdraw`, {
        amount: withdrawAmount.value,
        external_account_id: dest.account.id,
        idempotency_key: withdrawIdempotencyKey.value,
      })
    } else if (dest.type === 'crypto') {
      await API.post(`${HOST}/payments/bridge/organizations/${orgId.value}/withdraw-crypto`, {
        amount: withdrawAmount.value,
        crypto_address_id: dest.account.id,
        idempotency_key: withdrawIdempotencyKey.value,
      })
    }
    withdrawSuccess.value = true
    withdrawAmount.value = ''
    withdrawDest.value = ''
    withdrawConfirming.value = false
    showWithdrawForm.value = false
    fetchBalance()
  } catch (err: any) {
    withdrawError.value = err.response?.data?.detail || 'Withdrawal failed. Please try again.'
  } finally {
    withdrawLoading.value = false
  }
}

const fetchBalance = async () => {
  if (!bridgeOrg.value) return
  balanceLoading.value = true
  try {
    const res = await API.get(`${HOST}/payments/bridge/organizations/${orgId.value}/balance`)
    balance.value = res.data
  } catch {
    balance.value = null
  } finally {
    balanceLoading.value = false
  }
}

const allCustomers = ref<BridgeCustomer[]>([])
const allTransfers = ref<BridgeTransfer[]>([])
const transfersLoading = ref(false)

const externalAccounts = ref<ExternalAccount[]>([])
const externalAccountsLoading = ref(false)
const showConnectForm = ref(false)
const expandedAccount = ref<string | null>(null)
const toggleAccount = (key: string) => {
  expandedAccount.value = expandedAccount.value === key ? null : key
}
const revealedAddressId = ref<number | null>(null)
const copiedAddressId = ref<number | null>(null)
const copyAddress = async (id: number, address: string) => {
  try {
    await navigator.clipboard.writeText(address)
    copiedAddressId.value = id
    setTimeout(() => { copiedAddressId.value = null }, 2000)
  } catch {
    toast({ title: 'Copy failed', description: 'Could not access clipboard.', variant: 'destructive' })
  }
}

const virtualAccounts = ref<VirtualAccount[] | null>(null)
const virtualAccountsLoading = ref(false)

const usVirtualAccount = computed(() => {
  if (!virtualAccounts.value) return null
  const withDetails = virtualAccounts.value.filter(
    va => va.source_deposit_instructions?.bank_routing_number
      && va.source_deposit_instructions?.bank_account_number
  )
  if (!withDetails.length) return null
  return withDetails.find(va => va.destination?.currency === 'usdb') ?? withDetails[0]
})

// A VA exists but routing/account numbers haven't been assigned by Bridge yet
const usPendingVirtualAccount = computed(() => {
  if (!virtualAccounts.value || usVirtualAccount.value) return null
  return virtualAccounts.value.find(va =>
    va.destination?.currency === 'usdb' || va.source_deposit_instructions?.bank_name
  ) ?? null
})

const hasWireInstructions = computed(() => {
  const instr = usVirtualAccount.value?.source_deposit_instructions
  if (!instr) return false
  return !!(instr.swift_code || instr.payment_rails?.includes('wire'))
})

const fetchVirtualAccounts = async () => {
  virtualAccountsLoading.value = true
  try {
    const res = await API.get(`${HOST}/payments/bridge/organizations/${orgId.value}/virtual-accounts`)
    virtualAccounts.value = Array.isArray(res.data) ? res.data : []
  } catch {
    virtualAccounts.value = []
  } finally {
    virtualAccountsLoading.value = false
  }
}

const vaActivity = ref<any[] | null>(null)
const vaActivityLoading = ref(false)

const microdeposits = computed(() =>
  (vaActivity.value ?? []).filter(e => e.type === 'microdeposit')
)

const fetchVirtualAccountActivity = async (vaId: string) => {
  vaActivityLoading.value = true
  try {
    const res = await API.get(`${HOST}/payments/bridge/organizations/${orgId.value}/virtual-accounts/${vaId}/activity`)
    vaActivity.value = Array.isArray(res.data) ? res.data : []
  } catch {
    vaActivity.value = []
  } finally {
    vaActivityLoading.value = false
  }
}

const selfLink = ref<BridgeLink | null>(null)
const selfStatus = ref<'not_enrolled' | 'in_progress' | 'approved'>('not_enrolled')
const selfLoading = ref(false)
const selfRefreshing = ref(false)
const selfError = ref<string | null>(null)

const fetchSelfLink = async () => {
  selfLoading.value = true
  selfError.value = null
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/kyc-links/pending` })
    selfStatus.value = response.data.status
    selfLink.value = response.data.link
  } catch (e: any) {
    selfError.value = e.response?.data?.detail || 'Failed to load verification status'
  } finally {
    selfLoading.value = false
  }
}

const refreshSelfLink = async () => {
  selfRefreshing.value = true
  selfError.value = null
  try {
    const response = await API({ method: 'POST', url: `${HOST}/payments/bridge/me/sync` })
    selfStatus.value = response.data.status
    selfLink.value = response.data.link
  } catch (e: any) {
    const msg = e.response?.data?.detail || 'Failed to refresh verification status'
    selfError.value = msg
    toast({ title: 'Sync failed', description: msg, variant: 'destructive' })
  } finally {
    selfRefreshing.value = false
  }
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible' && selfMode.value) {
    refreshSelfLink()
  }
}

const orgAsCombinedCustomer = computed<CombinedCustomer | null>(() => {
  if (!bridgeOrg.value) return null
  return {
    type: 'business',
    id: bridgeOrg.value.id,
    bridge_customer_id: bridgeOrg.value.bridge_customer_id,
    name: bridgeOrg.value.organization_name || `Org #${bridgeOrg.value.organization_id}`,
    kyc_status: bridgeOrg.value.kyc_status,
    tos_status: bridgeOrg.value.tos_status,
    created_at: bridgeOrg.value.created_at,
  }
})

const orgMembers = computed<OrgMember[]>(() => store.getters['org/getCurrentMembers'] ?? [])

const orgCustomerIds = computed(() => {
  const memberIds = new Set(orgMembers.value.map(m => m.member_id))
  const ids = new Set(allCustomers.value.filter(c => memberIds.has(c.member_id)).map(c => c.id))
  if (bridgeOrg.value) ids.add(bridgeOrg.value.id)
  return ids
})

const orgTransfers = computed<BridgeTransfer[]>(() =>
  allTransfers.value.filter(
    t => orgCustomerIds.value.has(t.sender_customer_id) || (t.receiver_customer_id != null && orgCustomerIds.value.has(t.receiver_customer_id))
  )
)

type KybOverallStatus = 'approved' | 'rejected' | 'awaiting_ubo' | 'in_progress' | 'not_registered'

const kybStatus = computed<KybOverallStatus>(() => {
  if (!bridgeOrg.value) return 'not_registered'
  const kyb = bridgeOrg.value.kyc_status
  const tos = bridgeOrg.value.tos_status
  if (tos === 'approved' && kyb === 'approved') return 'approved'
  if (['rejected', 'paused', 'offboarded'].includes(kyb)) return 'rejected'
  if (kyb === 'awaiting_ubo') return 'awaiting_ubo'
  return 'in_progress'
})

const kycUrl = computed(() => bridgeOrg.value?.link?.kyc_url ?? null)
const tosUrl = computed(() => bridgeOrg.value?.link?.tos_url ?? null)

const fetchBridgeOrg = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/organizations` })
    const orgs: BridgeOrganization[] = response.data
    bridgeOrg.value = orgs.find(o => o.organization_id === orgId.value) ?? null
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to fetch payment status'
  } finally {
    loading.value = false
  }
}

const fetchBridgeOrgForAdmin = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/organizations/${orgId.value}/status` })
    bridgeOrg.value = response.data
  } catch (err: any) {
    if (err.response?.status === 404) {
      bridgeOrg.value = null
    } else {
      error.value = err.response?.data?.detail || 'Failed to fetch payment status'
    }
  } finally {
    loading.value = false
  }
}

const fetchExternalAccounts = async () => {
  if (!bridgeOrg.value) return
  externalAccountsLoading.value = true
  try {
    const response = await API.get(`${HOST}/payments/bridge/customers/${bridgeOrg.value.id}/external-accounts`)
    externalAccounts.value = Array.isArray(response.data) ? response.data : []
  } catch (err: any) {
    error.value = err?.response?.data?.detail || 'Failed to load external accounts'
    externalAccounts.value = []
  } finally {
    externalAccountsLoading.value = false
  }
}

const fetchOrgCustomers = async () => {
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/customers` })
    allCustomers.value = response.data
  } catch (e: any) {
    error.value = e.response?.data?.detail || 'Failed to load customers'
  }
}

const fetchTransfersData = async () => {
  transfersLoading.value = true
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/organizations/${orgId.value}/transfers`, params: { limit: 100 } })
    allTransfers.value = response.data
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to load transfers'
  } finally {
    transfersLoading.value = false
  }
}

onMounted(() => {
  if (isSuperUser.value) {
    fetchBridgeOrg()
    store.dispatch('org/fetchOrgMembers', orgId.value)
    fetchOrgCustomers()
    if (selfMode.value) refreshSelfLink()
  } else if (isOrgAdmin.value) {
    fetchBridgeOrgForAdmin()
    fetchVirtualAccounts()
  } else {
    refreshSelfLink()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

watch(selfMode, (enabled) => {
  if (enabled && !selfLink.value && !selfLoading.value) {
    fetchSelfLink()
  }
})

// Fallback: if isOrgAdmin wasn't ready at mount (current_member loads async), run org_admin setup
watch(isOrgAdmin, (isAdmin) => {
  if (!isAdmin) return
  if (!bridgeOrg.value && !loading.value) fetchBridgeOrgForAdmin()
  if (virtualAccounts.value === null && !virtualAccountsLoading.value) fetchVirtualAccounts()
})

watch(kybStatus, (status) => {
  // Auto-switch to People tab on initial load once KYB is confirmed approved
  if (status === 'approved' && activeTab.value === 'business' && !route.hash) activeTab.value = 'people'
  if (status !== 'approved' || (!isSuperUser.value && !isOrgAdmin.value)) return
  if (virtualAccounts.value === null && !virtualAccountsLoading.value) fetchVirtualAccounts()
  if (!balance.value && !balanceLoading.value) fetchBalance()
  if (externalAccounts.value.length === 0 && !externalAccountsLoading.value) fetchExternalAccounts()
  if (cryptoAddresses.value.length === 0 && !cryptoAddressesLoading.value) fetchCryptoAddresses()
})

watch(usVirtualAccount, (va) => {
  if (va && isOrgAdmin.value && vaActivity.value === null && !vaActivityLoading.value) {
    fetchVirtualAccountActivity(va.id)
  }
})

watch(() => route.hash, (hash) => {
  const tabFromHash = hash.replace(/^#/, '') as PaymentsTab
  if (VALID_TABS.includes(tabFromHash) && tabFromHash !== activeTab.value) {
    activeTab.value = tabFromHash
  }
})

watch(activeTab, (tab) => {
  const nextHash = `#${tab}`
  if (route.hash !== nextHash) {
    router.replace({ hash: nextHash })
  }
  if (tab === 'transfers') {
    if (allTransfers.value.length === 0 && !transfersLoading.value) fetchTransfersData()
  }
})
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- KYB onboarding drill-down sub-view -->
    <OrgKYBOnboarding
      v-if="view === 'kyb'"
      :bridge-org="bridgeOrg"
      :org-id="orgId"
      @back="view = 'status'"
    />

    <template v-else>

    <!-- Self KYC view: selfMode (any role) or regular members not yet approved -->
    <div v-if="selfMode || (!isSuperUser && !isOrgAdmin && (selfLoading || selfStatus !== 'approved'))" class="h-full flex flex-col overflow-hidden">
      <!-- Header with status badge -->
      <div class="px-4 py-2 border-b flex-shrink-0 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <button
            v-if="isSuperUser || isOrgAdmin"
            @click="selfMode = false"
            class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft class="h-3.5 w-3.5" />
            {{ isSuperUser ? 'Admin view' : 'Payments' }}
          </button>
          <span class="text-sm font-medium">My Identity Verification</span>
        </div>
        <button
          v-if="selfStatus === 'approved'"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200"
        >
          <CheckCircle2 class="h-3 w-3" />
          KYC Complete
        </button>
        <Badge
          v-else-if="selfStatus === 'in_progress'"
          class="bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100"
        >
          <CircleHelp class="h-3 w-3 mr-1" />
          In Progress
        </Badge>
        <Badge
          v-else-if="selfStatus === 'not_enrolled'"
          class="bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-100"
        >
          Not Enrolled
        </Badge>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto p-4">
        <div v-if="selfLoading" class="space-y-2">
          <div v-for="i in 2" :key="i" class="h-14 bg-muted rounded animate-pulse" />
        </div>

        <!-- Not enrolled -->
        <div v-else-if="selfStatus === 'not_enrolled'" class="rounded-lg border p-4 space-y-2 max-w-[600px]">
          <p class="text-xs text-muted-foreground">
            Your account has not been enrolled for payments yet. Contact your organization admin to get started.
          </p>
        </div>

        <!-- Enrolled: show steps -->
        <div v-else class="space-y-6 max-w-[600px]">
          <div>
            <h2 class="text-2xl font-bold">Identity Verification</h2>
            <p class="text-base text-muted-foreground mt-2">
              Complete two quick but important steps to enable payments. Buttons open in new tabs - return here after completing each one.
            </p>
          </div>

          <div class="rounded-lg bg-gray-50 border border-gray-200 shadow-lg p-6 space-y-4">
            <MemberKycSteps
              :link="selfLink"
              @refresh="refreshSelfLink"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- KYC-approved regular member: invoices only -->
    <div v-else-if="!isSuperUser && !isOrgAdmin && selfStatus === 'approved'" class="h-full flex flex-col overflow-hidden">
      <div class="px-4 py-2 border-b flex-shrink-0 flex items-center justify-between">
        <span class="text-sm font-medium">Invoices</span>
        <button
          @click="selfMode = true"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200"
        >
          <CheckCircle2 class="h-3 w-3" />
          KYC Complete
        </button>
      </div>
      <div class="flex-1 overflow-y-auto">
        <InvoicesTab />
      </div>
    </div>

    <!-- Admin / org-admin view: always 3 tabs -->
    <Tabs v-else v-model="activeTab" class="flex-1 flex flex-col overflow-hidden">

      <!-- Header -->
      <div class="px-4 py-2 border-b flex-shrink-0 flex items-center justify-between">
        <TabsList>
          <TabsTrigger value="business">Business</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="transfers">Transfers</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
        </TabsList>
        <button
          @click="selfMode = true"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 text-xs text-muted-foreground hover:text-foreground hover:border-gray-400 transition-colors"
        >
          <User class="h-3 w-3" />
          My KYC
        </button>
      </div>

      <!-- Business tab -->
      <TabsContent value="business" class="flex-1 overflow-y-auto mt-0 text-sm">

        <!-- KYB approved: business details -->
        <div v-if="kybStatus === 'approved'" class="p-4 space-y-6 max-w-[600px]">

          <!-- org title + KYB status badge -->
          <div class="flex items-center justify-between">
            <h2 class="font-semibold text-foreground">{{ bridgeOrg?.organization_name }} Payments</h2>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
              <CheckCircle2 class="h-3 w-3" />
              KYB Complete
            </span>
          </div>

          <!-- Balance + Withdraw -->
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <span class="font-medium text-muted-foreground uppercase tracking-wide">Balance</span>
              <div class="flex items-center gap-2">
                <span v-if="balanceLoading" class="text-muted-foreground">Loading...</span>
                <span v-else class="font-medium tabular-nums">
                  {{ parseFloat(balance?.balances?.find((b: any) => b.currency === 'usdb')?.balance ?? '0').toFixed(2) }} USD
                </span>
                <Button
                  v-if="hasAnyWithdrawDest && !showWithdrawForm"
                  size="sm"
                  @click="openWithdrawForm"
                >
                  Withdraw
                </Button>
              </div>
            </div>
            <!-- Step 1: destination + amount -->
            <div v-if="showWithdrawForm && !withdrawConfirming" class="border rounded p-3 space-y-2 bg-gray-50">
              <div class="font-medium">Withdraw</div>
              <select v-model="withdrawDest" class="w-full border rounded px-2 py-1.5 bg-white">
                <option value="" disabled>Select destination</option>
                <optgroup v-if="activeExternalAccounts.length > 0" label="Bank accounts (ACH)">
                  <option v-for="ea in activeExternalAccounts" :key="ea.id" :value="`ach:${ea.id}`">
                    {{ ea.bank_name }} — {{ ea.account_owner_name }}
                  </option>
                </optgroup>
                <optgroup v-if="cryptoAddresses.length > 0" label="Crypto wallets">
                  <option v-for="ca in cryptoAddresses" :key="ca.id" :value="`crypto:${ca.id}`">
                    {{ ca.label || (ca.address.slice(0, 10) + '...' + ca.address.slice(-6)) }} — {{ ca.chain }}{{ ca.verified ? '' : ' (unverified)' }}
                  </option>
                </optgroup>
              </select>
              <input v-model="withdrawAmount" type="number" min="1" step="0.01" placeholder="Amount (USD, min $1)" class="w-full border rounded px-2 py-1.5 bg-white" />
              <p v-if="withdrawAmountNum > availableBalance && withdrawAmountNum > 0" class="text-red-600">
                Amount exceeds available balance ({{ availableBalance.toFixed(2) }} USD).
              </p>
              <div v-if="withdrawError" class="text-red-600">{{ withdrawError }}</div>
              <div class="flex gap-2 justify-end">
                <button @click="showWithdrawForm = false" class="px-2 py-1 rounded border border-gray-300 text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                <button
                  @click="reviewWithdraw"
                  :disabled="!withdrawDest || withdrawAmountNum < 1 || withdrawAmountNum > availableBalance"
                  class="px-3 py-1 rounded bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-50"
                >
                  Review
                </button>
              </div>
            </div>
            <!-- Step 2: confirmation -->
            <div v-if="showWithdrawForm && withdrawConfirming" class="border rounded p-3 space-y-2 bg-amber-50 border-amber-200">
              <div class="font-medium">Confirm withdrawal</div>
              <div class="text-muted-foreground space-y-1">
                <div>Amount: <span class="font-medium text-foreground">${{ withdrawAmountNum.toFixed(2) }} USD</span></div>
                <template v-if="withdrawDestParsed?.type === 'ach'">
                  <div>To: <span class="font-medium text-foreground">{{ withdrawDestParsed.account.bank_name }} — {{ withdrawDestParsed.account.account_owner_name }}</span></div>
                </template>
                <template v-else-if="withdrawDestParsed?.type === 'crypto'">
                  <div>Chain: <span class="font-medium text-foreground capitalize">{{ withdrawDestParsed.account.chain }}</span></div>
                  <div v-if="withdrawDestParsed.account.label">To: <span class="font-medium text-foreground">{{ withdrawDestParsed.account.label }}</span></div>
                  <div class="font-mono break-all text-foreground">{{ withdrawDestParsed.account.address }}</div>
                </template>
              </div>
              <p v-if="withdrawDestParsed?.type === 'ach'" class="text-muted-foreground">Funds typically arrive in 1–2 business days and cannot be reversed once sent.</p>
              <p v-else-if="withdrawDestParsed?.type === 'crypto'" class="text-amber-700 font-medium">Double-check the address — crypto sends cannot be reversed.</p>
              <div v-if="withdrawError" class="text-red-600">{{ withdrawError }}</div>
              <div class="flex gap-2 justify-end">
                <button @click="cancelWithdraw" :disabled="withdrawLoading" class="px-2 py-1 rounded border border-gray-300 text-muted-foreground hover:text-foreground transition-colors">Back</button>
                <button @click="submitWithdraw" :disabled="withdrawLoading" class="px-3 py-1 rounded bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-50 flex items-center gap-1">
                  <Loader2 v-if="withdrawLoading" class="h-3 w-3 animate-spin" />
                  Confirm
                </button>
              </div>
            </div>
            <div v-if="withdrawSuccess" class="text-green-700 flex items-center gap-1">
              <CheckCircle2 class="h-3 w-3" />
              Withdrawal initiated.
            </div>
          </div>

          <!-- Connected Accounts -->
          <div class="space-y-1">
            <div class="flex items-center justify-between mb-2">
              <span class="font-medium text-muted-foreground uppercase tracking-wide">Connected Accounts</span>
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <Button size="sm">Connect</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem @click="showConnectForm = true">Bank account</DropdownMenuItem>
                  <DropdownMenuItem @click="showAddCryptoForm = true">Crypto wallet</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div v-if="externalAccountsLoading || cryptoAddressesLoading || virtualAccountsLoading" class="text-muted-foreground py-1">Loading...</div>
            <template v-else>

              <!-- Virtual account row (deposit account) -->
              <template v-if="usVirtualAccount || usPendingVirtualAccount">
                <div
                  class="flex items-center justify-between py-1.5 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                  @click="toggleAccount('va')"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Deposit</span>
                    <span class="font-medium truncate">USD Deposit Account</span>
                  </div>
                  <div class="flex items-center gap-2 shrink-0 ml-2">
                    <span v-if="usPendingVirtualAccount" class="text-xs text-amber-600">Pending</span>
                    <span v-else class="text-muted-foreground text-xs">ACH{{ hasWireInstructions ? ' · Wire' : '' }}</span>
                    <button
                      @click.stop="fetchVirtualAccounts"
                      :disabled="virtualAccountsLoading"
                      class="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                      title="Refresh"
                    >
                      <RefreshCw class="h-3 w-3" :class="{ 'animate-spin': virtualAccountsLoading }" />
                    </button>
                    <ChevronUp v-if="expandedAccount === 'va'" class="h-4 w-4 text-muted-foreground" /><ChevronDown v-else class="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                <!-- VA expanded detail -->
                <div v-if="expandedAccount === 'va'" class="px-1 pb-2 space-y-3">
                  <template v-if="usPendingVirtualAccount">
                    <p class="text-muted-foreground text-sm">Routing and account numbers are pending. Check back shortly.</p>
                  </template>
                  <template v-else-if="usVirtualAccount">
                    <!-- ACH -->
                    <div class="space-y-1">
                      <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">ACH</p>
                      <div class="rounded border bg-muted/40 p-3 space-y-1.5 font-mono text-sm">
                        <div v-if="usVirtualAccount.source_deposit_instructions.bank_name" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Bank</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_name }}</span>
                        </div>
                        <div v-if="usVirtualAccount.source_deposit_instructions.bank_beneficiary_name" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Account name</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_beneficiary_name }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Routing</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_routing_number }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Account</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_account_number }}</span>
                        </div>
                      </div>
                    </div>
                    <!-- Wire (if available) -->
                    <div v-if="hasWireInstructions" class="space-y-1">
                      <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">Wire</p>
                      <div class="rounded border bg-muted/40 p-3 space-y-1.5 font-mono text-sm">
                        <div v-if="usVirtualAccount.source_deposit_instructions.bank_name" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Bank</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_name }}</span>
                        </div>
                        <div v-if="usVirtualAccount.source_deposit_instructions.bank_address" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Bank address</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_address }}</span>
                        </div>
                        <div v-if="usVirtualAccount.source_deposit_instructions.swift_code" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">SWIFT / BIC</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.swift_code }}</span>
                        </div>
                        <div v-if="usVirtualAccount.source_deposit_instructions.bank_account_number" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Account</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_account_number }}</span>
                        </div>
                        <div v-if="usVirtualAccount.source_deposit_instructions.bank_beneficiary_name" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">Beneficiary</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.bank_beneficiary_name }}</span>
                        </div>
                        <div v-if="usVirtualAccount.source_deposit_instructions.iban" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">IBAN</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.iban }}</span>
                        </div>
                        <div v-if="usVirtualAccount.source_deposit_instructions.clabe" class="flex justify-between">
                          <span class="text-muted-foreground font-sans">CLABE</span>
                          <span>{{ usVirtualAccount.source_deposit_instructions.clabe }}</span>
                        </div>
                      </div>
                      <p class="text-amber-600 font-medium text-sm">USD only — your bank must send USD before wiring.</p>
                    </div>
                    <!-- Microdeposits -->
                    <div v-if="microdeposits.length > 0" class="space-y-1">
                      <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">Microdeposits</p>
                      <p class="text-sm text-muted-foreground">Enter these test deposit amounts in your bank portal to verify the connection:</p>
                      <div class="rounded border bg-muted/40 p-3 space-y-1.5">
                        <div v-for="(deposit, i) in microdeposits" :key="deposit.deposit_id || i" class="flex items-center justify-between text-sm">
                          <span class="text-muted-foreground">Test deposit {{ i + 1 }}</span>
                          <span class="font-mono font-medium">${{ parseFloat(deposit.amount).toFixed(2) }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- External bank account rows -->
              <template v-for="ea in activeExternalAccounts" :key="ea.id">
                <div
                  class="flex items-center justify-between py-1.5 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                  @click="toggleAccount(`ach:${ea.id}`)"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Bank</span>
                    <span class="font-medium truncate">{{ ea.bank_name }}</span>
                  </div>
                  <div class="flex items-center gap-2 shrink-0 ml-2">
                    <span class="text-muted-foreground text-xs">··{{ ea.account?.account_number?.toString().slice(-4) }}</span>
                    <ChevronUp v-if="expandedAccount === `ach:${ea.id}`" class="h-4 w-4 text-muted-foreground" />
                    <ChevronDown v-else class="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                <div v-if="expandedAccount === `ach:${ea.id}`" class="px-1 pb-2 space-y-1.5">
                  <div class="rounded border bg-muted/40 p-3 space-y-1.5 font-mono text-sm">
                    <div class="flex justify-between">
                      <span class="text-muted-foreground font-sans">Account owner</span>
                      <span>{{ ea.account_owner_name }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-muted-foreground font-sans">Type</span>
                      <span class="capitalize">{{ ea.account?.checking_or_savings ?? ea.account_type }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-muted-foreground font-sans">Account</span>
                      <span>··{{ ea.account?.account_number?.toString().slice(-4) }}</span>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Crypto wallet rows -->
              <template v-for="ca in cryptoAddresses" :key="ca.id">
                <div
                  class="flex items-center justify-between py-1.5 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                  @click="toggleAccount(`crypto:${ca.id}`)"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">{{ ca.chain }}</span>
                    <span class="font-medium truncate">{{ ca.label || (ca.address.slice(0, 8) + '...' + ca.address.slice(-6)) }}</span>
                  </div>
                  <div class="flex items-center gap-2 shrink-0 ml-2">
                    <span v-if="ca.verified" class="inline-flex items-center gap-0.5 text-green-700 text-xs">
                      <CheckCircle2 class="h-3 w-3" />
                      Verified
                    </span>
                    <ChevronUp v-if="expandedAccount === `crypto:${ca.id}`" class="h-4 w-4 text-muted-foreground" />
                    <ChevronDown v-else class="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                <div v-if="expandedAccount === `crypto:${ca.id}`" class="px-1 pb-2">
                  <div class="rounded border bg-muted/40 p-3 space-y-1.5 text-sm">
                    <div class="space-y-0.5">
                      <div class="flex items-center justify-between">
                        <span class="text-muted-foreground">Address</span>
                        <div class="flex items-center gap-1.5">
                          <button @click.stop="revealedAddressId = revealedAddressId === ca.id ? null : ca.id" class="text-muted-foreground hover:text-foreground transition-colors" :title="revealedAddressId === ca.id ? 'Hide' : 'Show'">
                            <EyeOff v-if="revealedAddressId === ca.id" class="h-3.5 w-3.5" />
                            <Eye v-else class="h-3.5 w-3.5" />
                          </button>
                          <span class="relative">
                            <button @click.stop="copyAddress(ca.id, ca.address)" class="text-muted-foreground hover:text-foreground transition-colors" title="Copy address">
                              <Clipboard class="h-3.5 w-3.5" />
                            </button>
                            <span v-if="copiedAddressId === ca.id" class="absolute right-0 top-5 z-10 whitespace-nowrap rounded bg-gray-800 text-white text-xs px-2 py-1 pointer-events-none">
                              Copied to clipboard!
                            </span>
                          </span>
                        </div>
                      </div>
                      <p class="font-mono break-all">{{ revealedAddressId === ca.id ? ca.address : (ca.address.slice(0, 10) + '••••••••••••••••••••••••' + ca.address.slice(-8)) }}</p>
                    </div>
                    <div class="space-y-0.5">
                      <span class="text-muted-foreground">Chain</span>
                      <p class="capitalize font-medium break-all">{{ ca.chain }}</p>
                    </div>
                    <div class="pt-1 flex items-center gap-2">
                      <button
                        v-if="ca.verified"
                        @click.stop="promptVerifyCryptoAddress(ca.id, false)"
                        class="text-xs px-2 py-1 rounded border border-gray-300 text-muted-foreground hover:text-foreground hover:border-gray-400 transition-colors"
                      >
                        Unverify
                      </button>
                      <button
                        v-else
                        @click.stop="promptVerifyCryptoAddress(ca.id, true)"
                        class="text-xs px-2 py-1 rounded border border-gray-300 text-muted-foreground hover:text-foreground hover:border-gray-400 transition-colors"
                      >
                        Mark Verified
                      </button>
                    </div>
                  </div>
                </div>
              </template>

              <p v-if="!usVirtualAccount && !usPendingVirtualAccount && activeExternalAccounts.length === 0 && cryptoAddresses.length === 0" class="text-muted-foreground py-1">
                No accounts connected. Add a bank account or crypto wallet to enable withdrawals.
              </p>
            </template>
          </div>

          <!-- Add bank account dialog -->
          <Dialog :open="showConnectForm" @update:open="(v: boolean) => { if (!v) showConnectForm = false }">
            <DialogContent class="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Connect bank account</DialogTitle>
              </DialogHeader>
              <ConnectExternalAccountForm
                v-if="showConnectForm && bridgeOrg"
                :customer-id="bridgeOrg.id"
                :embedded="true"
                @success="showConnectForm = false; fetchExternalAccounts()"
                @cancel="showConnectForm = false"
              />
            </DialogContent>
          </Dialog>

          <!-- Add crypto wallet dialog -->
          <Dialog :open="showAddCryptoForm" @update:open="(v: boolean) => { if (!v) { showAddCryptoForm = false; addCryptoError = null; newCryptoAddress = ''; newCryptoLabel = ''; newCryptoChain = 'base' } }">
            <DialogContent class="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Add crypto wallet</DialogTitle>
              </DialogHeader>
              <div class="space-y-4 pt-1">
                <div class="space-y-1.5">
                  <Label>Chain</Label>
                  <select v-model="newCryptoChain" class="w-full border rounded px-2 py-1.5 bg-white text-sm">
                    <option value="base">Base</option>
                    <option value="polygon">Polygon</option>
                  </select>
                </div>
                <div class="space-y-1.5">
                  <Label>Wallet address</Label>
                  <Input
                    v-model="newCryptoAddress"
                    placeholder="0x..."
                    class="font-mono"
                    :class="{ 'border-red-400': newCryptoAddress && !newCryptoAddressValid }"
                  />
                  <p v-if="newCryptoAddress && !newCryptoAddressValid" class="text-xs text-red-600">
                    Must be 0x followed by 40 hex characters.
                  </p>
                </div>
                <div class="space-y-1.5">
                  <Label>Label <span class="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input v-model="newCryptoLabel" placeholder="e.g. Kraken USDC" />
                </div>
                <p v-if="addCryptoError" class="text-xs text-red-600">{{ addCryptoError }}</p>
              </div>
              <DialogFooter>
                <Button variant="outline" :disabled="addCryptoLoading" @click="showAddCryptoForm = false; addCryptoError = null">Cancel</Button>
                <Button :disabled="!newCryptoAddressValid || addCryptoLoading" @click="submitAddCryptoAddress">
                  <Loader2 v-if="addCryptoLoading" class="h-4 w-4 animate-spin mr-1" />
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </div>

        <!-- KYB not yet approved: loading / not enrolled / in-progress / rejected -->
        <div v-else class="flex-1 flex items-center justify-center p-8">
          <div v-if="loading" class="space-y-4 w-full max-w-md">
            <div v-for="i in 3" :key="i" class="h-10 bg-muted rounded animate-pulse" />
          </div>
          <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm max-w-md w-full">
            {{ error }}
          </div>
          <div v-else-if="!bridgeOrg" class="text-center space-y-4 max-w-md">
            <Wallet class="h-12 w-12 text-muted-foreground mx-auto" />
            <h2 class="text-xl font-semibold">Payments not yet enabled</h2>
            <p class="text-muted-foreground">
              This organization has not been enrolled in payments yet. Contact Earth support to get started.
            </p>
          </div>
          <div v-else-if="kybStatus === 'rejected'" class="text-center space-y-4 max-w-md">
            <XCircle class="h-14 w-14 text-red-500 mx-auto" />
            <h2 class="text-2xl font-semibold">Verification not approved</h2>
            <p class="text-muted-foreground">KYB verification was not approved. Contact Bridge support to resolve.</p>
          </div>
          <div v-else class="text-center space-y-6 max-w-md">
            <div class="space-y-3">
              <Landmark class="h-14 w-14 text-muted-foreground mx-auto" />
              <h2 class="text-2xl font-semibold">Business Verification</h2>
              <Badge v-if="kybStatus === 'awaiting_ubo'" class="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100 text-sm px-3 py-1">
                Awaiting UBO
              </Badge>
            </div>
            <p class="text-muted-foreground text-base leading-relaxed">
              <template v-if="kybStatus === 'awaiting_ubo'">
                Waiting for Ultimate Beneficial Owner information to be submitted. Click below to continue.
              </template>
              <template v-else>
                Complete identity and business verification to enable payments for your organization.
              </template>
            </p>
            <p class="text-sm text-muted-foreground">
              A one-time $10 onboarding fee applies, billed through payments once onboarding is complete.
            </p>
            <div class="w-full space-y-3 text-left">
              <div class="flex items-center gap-4 rounded-lg border p-4">
                <div class="flex-shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">1</div>
                <div class="flex-1">
                  <div class="font-medium">Agree to Terms of Service</div>
                  <div class="text-sm text-muted-foreground">Review and accept Bridge's payment terms</div>
                </div>
                <CheckCircle2 v-if="bridgeOrg?.tos_status === 'approved'" class="h-6 w-6 text-green-500 flex-shrink-0" />
                <span v-else :title="tosUrl && !isBridgeUrlSafe(tosUrl) ? 'Production URL — disabled on local/dev' : undefined">
                  <Button size="sm" :disabled="!tosUrl || !isBridgeUrlSafe(tosUrl)" as-child>
                    <a v-if="tosUrl && isBridgeUrlSafe(tosUrl)" :href="tosUrl" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1.5">Open <ExternalLink class="h-3.5 w-3.5" /></a>
                    <span v-else class="inline-flex items-center gap-1.5">Open <ExternalLink class="h-3.5 w-3.5" /></span>
                  </Button>
                </span>
              </div>
              <div class="flex items-center gap-4 rounded-lg border p-4">
                <div class="flex-shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">2</div>
                <div class="flex-1">
                  <div class="font-medium">Verify Business</div>
                  <div class="text-sm text-muted-foreground">Identity and business verification via Persona</div>
                </div>
                <CheckCircle2 v-if="bridgeOrg?.kyc_status === 'approved'" class="h-6 w-6 text-green-500 flex-shrink-0" />
                <span v-else :title="kycUrl && !isBridgeUrlSafe(kycUrl) ? 'Production URL — disabled on local/dev' : undefined">
                  <Button size="sm" :disabled="!kycUrl || !isBridgeUrlSafe(kycUrl)" as-child>
                    <a v-if="kycUrl && isBridgeUrlSafe(kycUrl)" :href="kycUrl" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1.5">Open <ExternalLink class="h-3.5 w-3.5" /></a>
                    <span v-else class="inline-flex items-center gap-1.5">Open <ExternalLink class="h-3.5 w-3.5" /></span>
                  </Button>
                </span>
              </div>
            </div>
          </div>
        </div>

      </TabsContent>

      <!-- People tab -->
      <TabsContent value="people" class="flex-1 overflow-y-auto p-4 space-y-6 mt-0">
        <div v-if="loading" class="space-y-3">
          <div class="h-4 bg-muted rounded animate-pulse w-1/2" />
          <div class="h-20 bg-muted rounded animate-pulse" />
        </div>
        <div v-else-if="error" class="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
          {{ error }}
        </div>
        <OrgPaymentsMembersList v-else :org-id="orgId" />
      </TabsContent>

      <!-- Transfers tab -->
      <TabsContent value="transfers" class="flex-1 overflow-y-auto p-4 mt-0 space-y-6">
        <div v-if="!transfersLoading && orgTransfers.length === 0" class="text-xs text-muted-foreground mb-2">
          No transfers found for this organization.
        </div>
        <BridgeTransfersList
          :transfers="orgTransfers"
          :loading="transfersLoading"
          :allow-cancel="true"
          @transfer-cancelled="(t) => { const i = allTransfers.findIndex(x => x.id === t.id); if (i !== -1) allTransfers[i] = t }"
        />
      </TabsContent>

      <!-- Invoices tab -->
      <TabsContent value="invoices" class="flex-1 overflow-y-auto mt-0">
        <InvoicesTab />
      </TabsContent>

    </Tabs>

    <DialogConfirm
      ref="confirmVerifyDialog"
      :title="pendingVerifyValue ? 'Verify wallet address' : 'Unverify wallet address'"
      :description="pendingVerifyValue
        ? 'Mark this wallet as verified? Only verify after confirming a successful test transfer.'
        : 'Remove verified status from this wallet address?'"
      :confirm-text="pendingVerifyValue ? 'Mark as verified' : 'Mark as unverified'"
      @confirm="verifyCryptoAddress"
    />

    </template>
  </div>
</template>
